import { useEffect, useRef, useState } from 'react';
import HTMLParser from 'html-react-parser';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type { StripeCardNumberElementChangeEvent } from '@stripe/stripe-js';
import { AccountPaymentMethodAddApiPropertiesModel } from '@/services/platform/api/model';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Icon } from '@/components/ui/icon';
import { FormField } from '@/components/ui/form-field';
import { Switch } from '@/components/ui/switch';
import { ScreenHead } from '@/components/shared/screen/head';
import { fieldForStripeError, StripeField, type CardField } from '@/components/shared/payment/card-fields';
import { ConfirmationSheet } from '@/components/shared/sheets/confirmation';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { cn, useClientTranslation } from '@/lib/core';
import { useUIStore } from '@/lib/core/stores';
import { createSetupIntent, getPaymentMethods } from '@/services/platform/data/payment-methods';
import { CardBrands } from './card-brands';
import { PaymentStripeProvider, STRIPE_ELEMENT_STYLE } from './stripe-provider';

interface ProfilePaymentAddProps {
  properties: AccountPaymentMethodAddApiPropertiesModel | undefined;
  /** Screen title, from the CMS page name where available. */
  title: string;
  onBack: () => void;
  /** Called after Stripe has attached the card. Receives the new Stripe payment method id. */
  onSaved: (paymentMethodId: string, isPreferred: boolean) => Promise<void>;
  onDiscarded: () => void;
  /**
   * Called when saving the card fails, so the host can raise a toast. The inline per-field and
   * form-level messages are rendered by this component regardless.
   */
  onError?: (message: string) => void;
  /**
   * Hides this component's own `ScreenHead`.
   *
   * Set when the form is rendered inside `DefaultScreenLayout`, which already draws a fixed header
   * for the page — without this the standalone add page shows two stacked headers.
   */
  hideHeader?: boolean;
}

/** The form body — separate component so it can sit inside `<Elements>` and call `useStripe`. */
function AddPaymentForm({
  properties,
  title,
  onBack,
  onSaved,
  onDiscarded,
  onError,
  hideHeader,
  clientSecret,
  isFirstCard,
}: ProfilePaymentAddProps & { clientSecret: string; isFirstCard: boolean }) {
  const t = useClientTranslation();
  const stripe = useStripe();
  const elements = useElements();

  const [holderName, setHolderName] = useState('');
  // The first card has to end up preferred: nothing else sets the Stripe default, so leaving this
  // free would let a customer finish with saved cards and no preferred one at all. Also locked on
  // when the card-count read failed — an unknown count must default to the safer outcome, since the
  // alternative is a customer silently ending up with zero preferred cards. From the second card
  // onward the choice is real and the switch is live.
  const preferredLocked = isFirstCard;
  const [isPreferred, setIsPreferred] = useState(false);
  const preferred = preferredLocked || isPreferred;
  const [cardComplete, setCardComplete] = useState(false);
  const [expiryComplete, setExpiryComplete] = useState(false);
  const [cvcComplete, setCvcComplete] = useState(false);
  // Tracked separately from `complete`: a half-typed card number is neither complete nor invalid,
  // but it is still unsaved work, so backing out of it must warn.
  const [cardTouched, setCardTouched] = useState({ number: false, expiry: false, cvc: false });
  // Per-field validation messages, supplied by Stripe and already localised — shown verbatim.
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<CardField, string>>>({});
  const [cardError, setCardError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);
  // Set on a blocked save so "required but empty" fields can be flagged, not just invalid ones.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const isValid = cardComplete && expiryComplete && cvcComplete && !!holderName.trim();
  // Any partially entered value counts as unsaved work — the Stripe fields report `empty`, so a
  // number typed halfway makes the form dirty even though it is not yet `complete`.
  //
  // Uses the raw `isPreferred`, never the derived `preferred`: a locked-on first card is not
  // something the customer did, so it must not make an otherwise-untouched form dirty and raise the
  // discard sheet on a back tap that never touched anything.
  const isDirty =
    cardTouched.number || cardTouched.expiry || cardTouched.cvc || !!holderName.trim() || isPreferred;

  const setScreenHeadOverride = useUIStore(state => state.setScreenHeadOverride);

  // With `hideHeader`, the visible header is `DefaultScreenLayout`'s, whose arrow calls
  // `navigate(-1)` and knows nothing about this form. Overriding `onBack` is how a module
  // intercepts it — `ScreenHead` merges `{ ...props, ...override }`.
  //
  // Registered once, reading through a ref: `isDirty` flips on the first keystroke in every field
  // and `onBack` is a fresh closure on each parent render, so depending on either would
  // re-register constantly — or capture a stale `onBack` if omitted from the deps.
  const guard = useRef({ isDirty, onBack, setShowDiscard });
  guard.current = { isDirty, onBack, setShowDiscard };

  useEffect(() => {
    if (!hideHeader) return;

    // Only `onBack` is overridden. The spread in `ScreenHead` copies own enumerable keys, so a
    // `title` key here — even one set to `undefined` — would blank the page heading.
    setScreenHeadOverride({
      onBack: () => {
        const { isDirty: dirty, onBack: back, setShowDiscard: show } = guard.current;
        if (dirty) show(true);
        else back();
      },
    });

    return () => setScreenHeadOverride(null);
  }, [hideHeader, setScreenHeadOverride]);

  /** Records (or clears) a field's error as the user types. */
  function setFieldError(field: CardField, message?: string) {
    setFieldErrors(previous => {
      if (previous[field] === message) return previous;
      const next = { ...previous };
      if (message) next[field] = message;
      else delete next[field];
      return next;
    });
  }

  // The CTA stays tappable once the user has engaged, so a tap can explain what is wrong rather
  // than dead-ending on a greyed-out button. Only an untouched form disables it.
  const canAttemptSave = isDirty && !saving;

  // Stripe's message wins; after a blocked save attempt, a still-incomplete field is flagged as
  // required so the user knows which one to finish.
  const requiredMessage = t('App.Labels.Required');
  const numberError = fieldErrors.number ?? (submitAttempted && !cardComplete ? requiredMessage : undefined);
  const expiryError =
    fieldErrors.expiry ?? (submitAttempted && !expiryComplete ? requiredMessage : undefined);
  const cvcError = fieldErrors.cvc ?? (submitAttempted && !cvcComplete ? requiredMessage : undefined);
  const nameError = submitAttempted && !holderName.trim() ? requiredMessage : undefined;

  async function handleSave() {
    setSubmitAttempted(true);

    // An incomplete or invalid form surfaces its per-field messages instead of submitting; the
    // fields already carry Stripe's own text, and `submitAttempted` reveals the empty ones.
    if (!isValid || saving || !stripe || !elements) return;

    const cardElement = elements.getElement(CardNumberElement);
    if (!cardElement) return;

    setSaving(true);
    setCardError(null);

    try {
      // Card data goes straight from the browser to Stripe — it never reaches the Amorim API.
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: cardElement, billing_details: { name: holderName.trim() } },
      });

      if (result.error) {
        const message = result.error.message ?? t('App.Labels.PaymentCardError');
        const field = fieldForStripeError(result.error.param, result.error.code);

        // Attribute the failure to the offending input when Stripe identifies one; otherwise it
        // stays a form-level message (declines with no parameter, network failures, and so on).
        if (field) setFieldError(field, message);
        else setCardError(message);
        onError?.(message);

        // Nothing is cleared: the SetupIntent survives a failed confirm, so the same client secret
        // is reused and the user only has to correct what was wrong.
        return;
      }

      const paymentMethodId = result.setupIntent?.payment_method;
      if (typeof paymentMethodId !== 'string') {
        setCardError(t('App.Labels.PaymentCardError'));
        onError?.(t('App.Labels.PaymentCardError'));
        return;
      }

      await onSaved(paymentMethodId, preferred);
    } catch (error) {
      console.warn('AddPaymentForm: confirmCardSetup failed:', error);
      setCardError(t('App.Labels.PaymentCardError'));
      onError?.(t('App.Labels.PaymentCardError'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="flex h-full flex-col">
        <div className="flex-1 overflow-y-auto px-4 pb-8">
          {!hideHeader && (
            <ScreenHead
              title={title}
              onBack={() => (isDirty ? setShowDiscard(true) : onBack())}
              ariaLabel={t('App.Labels.GoBack')}
              className="px-0 pt-0! pb-6"
            />
          )}

          {properties?.subTitle?.markup && (
            <div className="font-pp-neue-montreal text-jncquoi-neutral-1000 mb-4 text-[20px] leading-[120%] font-semibold">
              {HTMLParser(properties.subTitle.markup)}
            </div>
          )}

          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <FormField
                id="card-number"
                label={t('App.Labels.PaymentCardNumber')}
                required
                error={numberError}
              >
                <StripeField error={!!numberError}>
                  <CardNumberElement
                    options={{
                      style: STRIPE_ELEMENT_STYLE,
                      placeholder: t('App.Labels.PaymentCardNumberPlaceholder'),
                    }}
                    onChange={(event: StripeCardNumberElementChangeEvent) => {
                      setCardComplete(event.complete);
                      setCardTouched(previous => ({ ...previous, number: !event.empty }));
                      setFieldError('number', event.error?.message);
                      setCardError(null);
                    }}
                  />
                </StripeField>
              </FormField>

              <div className="flex items-start gap-4">
                <FormField
                  id="card-expiry"
                  label={t('App.Labels.ExpirationDate')}
                  required
                  error={expiryError}
                  className="min-w-0 flex-1"
                >
                  <StripeField error={!!expiryError}>
                    <CardExpiryElement
                      options={{
                        style: STRIPE_ELEMENT_STYLE,
                        placeholder: t('App.Labels.ExpirationDatePlaceholder'),
                      }}
                      onChange={event => {
                        setExpiryComplete(event.complete);
                        setCardTouched(previous => ({ ...previous, expiry: !event.empty }));
                        setFieldError('expiry', event.error?.message);
                      }}
                    />
                  </StripeField>
                </FormField>

                <FormField
                  id="card-cvc"
                  label={t('App.Labels.Cvv')}
                  required
                  error={cvcError}
                  className="min-w-0 flex-1"
                >
                  <StripeField hasIcon error={!!cvcError}>
                    <CardCvcElement
                      options={{
                        style: STRIPE_ELEMENT_STYLE,
                        placeholder: t('App.Labels.CvvPlaceholder'),
                      }}
                      onChange={event => {
                        setCvcComplete(event.complete);
                        setCardTouched(previous => ({ ...previous, cvc: !event.empty }));
                        setFieldError('cvc', event.error?.message);
                      }}
                    />
                    <div className="absolute right-3.75 flex items-center">
                      <Icon
                        name="help-circle"
                        width={20}
                        height={20}
                        aria-label={t('App.Labels.CvvHelp')}
                        className="text-jncquoi-neutral-1000 shrink-0"
                      />
                    </div>
                  </StripeField>
                </FormField>
              </div>

              <FormField id="holder-name" label={t('App.Labels.NameOnCard')} required error={nameError}>
                <Input
                  id="holder-name"
                  value={holderName}
                  placeholder={t('App.Labels.NameOnCardPlaceholder')}
                  autoComplete="cc-name"
                  error={!!nameError}
                  onChange={event => setHolderName(event.target.value)}
                />
              </FormField>
            </div>

            <div className="flex flex-col gap-4">
              <label className={cn('flex items-start gap-2', preferredLocked && 'cursor-default')}>
                <Switch
                  checked={preferred}
                  onCheckedChange={setIsPreferred}
                  disabled={preferredLocked}
                  className="mt-0.5"
                />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <p
                    className={cn(
                      'font-pp-neue-montreal text-[14px] tracking-[0.5px]',
                      preferredLocked ? 'text-jncquoi-neutral-700' : 'text-jncquoi-neutral-1000'
                    )}
                  >
                    {t('App.Labels.PreferredPaymentMethod')}
                  </p>
                  {preferredLocked && (
                    <p className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[12px] tracking-[0.5px]">
                      {t('App.Labels.PreferredPaymentMethodFirstCard')}
                    </p>
                  )}
                </div>
              </label>

              {properties?.privacyPolicyDisclaimer?.markup && (
                <div className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[12px] tracking-[0.5px] [&_a]:font-semibold [&_a]:underline">
                  {HTMLParser(properties.privacyPolicyDisclaimer.markup)}
                </div>
              )}
            </div>

            <CardBrands
              label={properties?.acceptedCardsLabel ?? t('App.Labels.AcceptedCards')}
              images={properties?.acceptedCardsImages}
            />
          </div>
        </div>

        <div
          className="border-jncquoi-neutral-400 shrink-0 border-t bg-white px-4 pt-6"
          style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.5rem)' }}
        >
          {/* Sits by the CTA so a decline is visible without scrolling back up the form. */}
          {cardError && (
            <p
              role="alert"
              className="text-jncquoi-asia-7427c-500 font-pp-neue-montreal mb-3 text-center text-xs tracking-[0.5px]"
            >
              {cardError}
            </p>
          )}
          <Button
            variant="primary-solid"
            palette="dark"
            size="lg"
            className="w-full"
            disabled={!canAttemptSave}
            loading={saving}
            onClick={handleSave}
          >
            {t('App.Labels.SavePaymentMethod')}
          </Button>
        </div>
      </div>

      <ConfirmationSheet
        open={showDiscard}
        onOpenChange={setShowDiscard}
        onConfirm={async () => {}}
        onSuccess={onDiscarded}
        headerTitle={t('App.Labels.Confirmation')}
        title={t('App.Labels.DiscardPaymentMethodTitle')}
        description={t('App.Labels.DiscardPaymentMethodDescription')}
        confirmLabel={t('App.Labels.DiscardPaymentMethod')}
        dismissLabel={t('App.Labels.ContinueEditing')}
        silentSuccess
      />
    </>
  );
}

/**
 * "Add payment method" screen.
 *
 * A SetupIntent is created on mount so Stripe Elements can bind to its client secret immediately;
 * the publishable key travels with the same response, so Stripe is only loaded once it arrives.
 */
export function ProfilePaymentAdd(props: ProfilePaymentAddProps) {
  const t = useClientTranslation();
  const [intent, setIntent] = useState<{ clientSecret: string; publishableKey: string } | null>(null);
  const [loading, setLoading] = useState(true);
  // Locked-on by default (see `preferredLocked` in `AddPaymentForm`): a card-count read that fails
  // must resolve to the safer outcome, since the alternative is a customer silently ending up with
  // one or more saved cards and none of them preferred. Only an explicit non-empty list unlocks it.
  const [isFirstCard, setIsFirstCard] = useState(true);

  useEffect(() => {
    let isStale = false;

    Promise.all([createSetupIntent(), getPaymentMethods()])
      .then(([result, cards]) => {
        if (isStale) return;
        if (result?.clientSecret && result.publishableKey) {
          setIntent({ clientSecret: result.clientSecret, publishableKey: result.publishableKey });
        }
        // `getPaymentMethods` returns `undefined` on a failed read, distinct from `[]` (genuinely no
        // cards) — only the latter proves there is an existing preferred card to defer to.
        setIsFirstCard(cards === undefined || cards.length === 0);
      })
      .finally(() => {
        if (!isStale) setLoading(false);
      });

    return () => {
      isStale = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex h-full flex-col">
        {!props.hideHeader && (
          <ScreenHead
            title={props.title}
            onBack={props.onBack}
            ariaLabel={t('App.Labels.GoBack')}
            className="px-4 pt-0! pb-6"
          />
        )}
        <SpinLoading wrapperClassName="flex-1 min-h-[30vh]" />
      </div>
    );
  }

  // No SetupIntent means Stripe is unreachable or unconfigured — the form can't collect a card,
  // so say so rather than rendering fields that could never save.
  if (!intent) {
    return (
      <div className="flex h-full flex-col">
        {!props.hideHeader && (
          <ScreenHead
            title={props.title}
            onBack={props.onBack}
            ariaLabel={t('App.Labels.GoBack')}
            className="px-4 pt-0! pb-6"
          />
        )}
        <div className="flex flex-1 items-center justify-center px-4">
          <p
            role="alert"
            className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-center text-[14px] tracking-[0.5px]"
          >
            {t('App.Labels.PaymentSetupUnavailable')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <PaymentStripeProvider publishableKey={intent.publishableKey} clientSecret={intent.clientSecret}>
      <AddPaymentForm {...props} clientSecret={intent.clientSecret} isFirstCard={isFirstCard} />
    </PaymentStripeProvider>
  );
}
